'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import type { InboxNotification } from '@/lib/api/notifications/inbox-client';
import {
  useInboxNotifications,
  useInboxUnreadCount,
  useMarkAllInboxNotificationsRead,
} from '@/lib/api/notifications/inbox-hooks';
import { DialogAccountActivities } from '@/lib/components/Account/Settings/DialogAccountActivities';
import {
  NotificationItem,
  NotificationItemSkeleton,
  useFreshItemIds,
} from '@/lib/components/Notifications/NotificationItem';
import { Link } from '@/lib/routing';
import { Drawer, DrawerClose, DrawerContent, DrawerTitle } from '@/lib/ui-kit/drawer';
import { Icon } from '@/lib/ui-kit/icons';
import { Button } from '@/lib/ui-kit-v2/button';

interface NotificationsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dev-only: bypass hooks and render a fixed dataset. */
  mockOverride?: {
    items: InboxNotification[];
    unreadCount: number;
  };
}

export function NotificationsDrawer({
  open,
  onOpenChange,
  mockOverride,
}: NotificationsDrawerProps) {
  const tNav = useTranslations('nav');
  const tNotifs = useTranslations('notifications');
  const locale = useLocale();
  const query = useInboxNotifications({
    limit: 20,
    enabled: open && !mockOverride,
  });
  const unread = useInboxUnreadCount({ enabled: open && !mockOverride });
  const markAllRead = useMarkAllInboxNotificationsRead();
  const liveItems = query.data?.pages.flatMap((page) => page.items) ?? [];
  // Drawer is intentionally capped — it's a "recent" view, never a paginated
  // list. Anything beyond the cap belongs on /notifications.
  const DRAWER_ITEM_CAP = 20;
  const items = (mockOverride?.items ?? liveItems).slice(0, DRAWER_ITEM_CAP);
  const isLoading = !mockOverride && query.isLoading;
  const isEmpty = !isLoading && items.length === 0;
  const unreadCount = mockOverride?.unreadCount ?? unread.data?.count ?? 0;
  const hasUnread = unreadCount > 0;
  const freshIds = useFreshItemIds(items);

  // Below 768px (project's `sm` breakpoint) the drawer slides up from the
  // bottom instead of in from the right. Default to false for SSR so the
  // initial markup is desktop-shaped; useEffect upgrades it on mount.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const direction = isMobile ? 'bottom' : 'right';

  // New-login notifications open this modal instead of navigating. State lives
  // here (not in the unmounting DrawerContent) so it survives the drawer close.
  const [activitiesOpen, setActivitiesOpen] = useState(false);

  // When a mock dataset is supplied (dev/preview pages), keep the drawer open
  // for click-throughs on neighbouring dev controls (e.g. "Simulate INBOX_NEW").
  const isDevPreview = Boolean(mockOverride);

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        direction={direction}
        modal={!isDevPreview}
        dismissible={!isDevPreview}
      >
        <DrawerContent
          noOverlay={isDevPreview}
          className="!w-full flex-col bg-v2-surface-primary text-v2-text-primary sm:!max-w-[520px] sm:border-l sm:border-v2-border max-sm:h-[50vh] max-sm:rounded-t-[16px] max-sm:border-t max-sm:border-v2-border"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 px-8 pt-8 pb-0 max-sm:gap-6 max-sm:px-4 max-sm:pt-6">
            <div className="flex shrink-0 items-center gap-2.5">
              <DrawerTitle className="flex-1 font-semibold text-[24px] text-v2-text-primary leading-[32px]">
                {tNav('notifications')}
                {hasUnread ? (
                  <span className="ml-2 text-v2-text-secondary">[{unreadCount}]</span>
                ) : null}
              </DrawerTitle>
              {hasUnread ? (
                <Button
                  appearance="ghost"
                  size="M"
                  onClick={() => markAllRead.mutate()}
                  disabled={markAllRead.isPending}
                  className="font-medium text-v2-text-secondary hover:bg-transparent"
                >
                  {tNotifs('readAll')}
                </Button>
              ) : null}
              <DrawerClose
                aria-label={tNav('close')}
                className="inline-flex size-10 shrink-0 items-center justify-center rounded-full text-v2-text-secondary outline-none transition-colors hover:bg-v2-surface-tertiary hover:text-v2-text-primary focus-visible:ring-2 focus-visible:ring-v2-border"
              >
                <Icon name="Close" size={24} />
              </DrawerClose>
            </div>

            {isLoading ? (
              <ul className="flex-1 overflow-y-auto px-3 max-sm:-mx-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {Array.from({ length: 6 }).map((_, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders are stable for the duration of the loading state
                  <NotificationItemSkeleton key={idx} />
                ))}
              </ul>
            ) : isEmpty ? (
              <div className="flex w-full flex-col items-center gap-4 rounded-[10px] px-4 py-6 text-v2-text-secondary">
                <Icon name="Search" size={20} />
                <p className="w-[236px] text-center text-[12px] leading-[1.5]">
                  {tNotifs('empty')}
                </p>
              </div>
            ) : (
              <div className="relative flex min-h-0 flex-1 flex-col">
                <ul className="flex-1 overflow-y-auto px-3 max-sm:-mx-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {items.map((item) => (
                    <NotificationItem
                      key={item.id}
                      item={item}
                      locale={locale}
                      freshIndex={freshIds.get(item.id)}
                      onClick={() => onOpenChange(false)}
                      onOpenAccountActivities={() => setActivitiesOpen(true)}
                    />
                  ))}
                </ul>
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-v2-surface-primary to-transparent"
                />
              </div>
            )}
          </div>

          {!isEmpty ? (
            <Link
              href="/notifications"
              onClick={() => onOpenChange(false)}
              className="flex shrink-0 items-center justify-center gap-2 border-v2-border border-t px-4 py-3 font-medium text-[13px] text-v2-text-primary leading-[16px] tracking-[-0.13px] outline-none transition-colors hover:bg-v2-surface-secondary focus-visible:bg-v2-surface-secondary"
            >
              <span className="leading-[14px]">{tNotifs('viewAll')}</span>
              <span className="inline-flex items-center text-v2-text-secondary leading-none">
                <Icon name="ChevronRight" size={14} />
              </span>
            </Link>
          ) : null}
        </DrawerContent>
      </Drawer>
      <DialogAccountActivities isOpen={activitiesOpen} setIsOpen={setActivitiesOpen} />
    </>
  );
}
